import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Divider,
    IconButton,
    FormHelperText,
    Autocomplete,
    Avatar,
    Stack,
} from "@mui/material";
import { Cancel, Save, Add, Remove } from "@mui/icons-material";
import { Company, ProductPlan } from "../../Interfaces/Company";
import { Logger } from '../../../utils/Logger';
import { apiClient } from '../../../utils/ApiClient';
import { ProductPlanPanel } from '../../../components/ProductPlanPanel';

/** Same shape as signup (`Login.tsx`) — populated from `GET /auth/countries` */
interface CountryOption {
    code: string;
    label: string;
    dialCode: string;
}
import { FlexBetween } from '../../../components/FlexBetween';
import { useSystemModules } from '../../../hooks/useSystemModules';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';

interface CompanyDialogProps {
    open: boolean;
    onClose: () => void;
    company: Partial<Company> | null;
    isEditing: boolean;
    onSave: () => void;
    saveLoading: boolean;
}

// License Type options
const LICENSE_TYPES = [
    'Enterprise',
    'BusinessPlus', 
    'Business',
    'VerifiedPro',
    'PublicFree'
] as const;



// Helper function to convert ISO date string to YYYY-MM-DD format
const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
        Logger.error('Error formatting date:', error);
        return '';
    }
};

// Helper function to convert YYYY-MM-DD format back to ISO string
const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString + 'T00:00:00.000Z');
        return date.toISOString();
    } catch (error) {
        Logger.error('Error formatting date for API:', error);
        return '';
    }
};

export const CompanyDialog: React.FC<CompanyDialogProps> = ({
    open,
    onClose,
    company,
    isEditing,
    onSave,
    saveLoading
}) => {
    // Use Redux + existing permission helper to determine super admin status reliably
    const user = useSelector((state: RootState) => state.authentication.user);
    const { isUserSuperAdmin } = useModulePermissions();
    const { modules, loading: modulesLoading, error: modulesError } = useSystemModules(isUserSuperAdmin);
    
    // Fallback modules if the API call fails
    const fallbackModules = ['instantupdate.ai', 'automatetest.ai', 'assetsmanagement.ai', 'infralytiqs.com', 'anamnesis.cloud', 'beamonscreen.com'];
    const availableModules = modules.length > 0 ? modules : fallbackModules;
    const [formData, setFormData] = useState<Partial<Company>>({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
        companyEmail: '',
        website: '',
        taxcode: '',
        companyPhone: '',
        registrationNumber: '',
        productPlans: [],
        configurations: {
            defaultBotManager: ''
        }
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [lightLogoPreview, setLightLogoPreview] = useState<string | undefined>(undefined);
    const [darkLogoPreview, setDarkLogoPreview] = useState<string | undefined>(undefined);
    const [lightLogoRemoved, setLightLogoRemoved] = useState<boolean>(false);
    const [darkLogoRemoved, setDarkLogoRemoved] = useState<boolean>(false);
    const [logoErrors, setLogoErrors] = useState<{ light?: string; dark?: string }>({});
    const [logoWarnings, setLogoWarnings] = useState<{ light?: string; dark?: string }>({});
    const [workerPoolNames, setWorkerPoolNames] = useState<string[]>([]);
    const [workerPoolNamesLoading, setWorkerPoolNamesLoading] = useState<boolean>(false);
    const [workerPoolNamesError, setWorkerPoolNamesError] = useState<string | null>(null);

    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [countriesError, setCountriesError] = useState<string | null>(null);

    const bufferLikeToDataUrl = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === 'string') return value;
        // Handle Node Buffer JSON shape: { type: 'Buffer', data: number[] }
        if (value?.type === 'Buffer' && Array.isArray(value?.data)) {
            try {
                const u8 = new Uint8Array(value.data as number[]);
                let binary = '';
                for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);
                const base64 = btoa(binary);
                return `data:image/png;base64,${base64}`;
            } catch (e) {
                Logger.error('CompanyDialog', 'Failed to convert buffer to data URL');
                return undefined;
            }
        }
        return undefined;
    };

    useEffect(() => {
        if (company) {
            // Convert ISO date strings to YYYY-MM-DD format for date inputs
            const processedCompany = { ...company } as any;
            if (processedCompany.productPlans) {
                processedCompany.productPlans = processedCompany.productPlans.map((plan: any) => {
                    const normalizedPlan: any = {
                        ...plan,
                        startedAt: formatDateForInput(plan.startedAt),
                        endsAt: formatDateForInput(plan.endsAt),
                    };

                    // Normalize legacy flat quota numbers into nested objects for UI
                    const ensureQuota = (existing: any, legacyValue: number | undefined) => {
                        if (existing && (existing.perDayLimit !== undefined)) return existing;
                        if (legacyValue === undefined || legacyValue === null) return existing || undefined;
                        return { perDayLimit: legacyValue };
                    };

                    normalizedPlan.whatsApp = ensureQuota(plan.whatsApp, plan.whatsAppMessagesAllowedPerDay);
                    normalizedPlan.whatsAppTemplate = ensureQuota(plan.whatsAppTemplate, plan.whatsAppTemplatesAllowedPerDay);
                    normalizedPlan.gsmText = ensureQuota(plan.gsmText, plan.gsmTextMessagesAllowedPerDay);
                    normalizedPlan.gsmTextTemplate = ensureQuota(plan.gsmTextTemplate, plan.gsmTextTemplatesAllowedPerDay);
                    normalizedPlan.telegram = ensureQuota(plan.telegram, plan.telegramMessagesPerDay);
                    normalizedPlan.telegramTemplate = ensureQuota(plan.telegramTemplate, plan.telegramTemplatesPerDay);
                    normalizedPlan.email = ensureQuota(plan.email, plan.emailsAllowedPerDay);
                    normalizedPlan.emailTemplate = ensureQuota(plan.emailTemplate, plan.emailTemplatesAllowedPerDAy);

                    if (plan.totalSizeInPlan == null && plan.sizeOfDAMinGB != null) {
                        normalizedPlan.totalSizeInPlan = plan.sizeOfDAMinGB;
                    }

                    return normalizedPlan as ProductPlan;
                });
            }
            setFormData(processedCompany);
            // Initialize logo previews from incoming company data if present (string or Buffer-like)
            const light = bufferLikeToDataUrl(processedCompany.brand?.logo?.light as any);
            const dark = bufferLikeToDataUrl(processedCompany.brand?.logo?.dark as any);
            setLightLogoPreview(light);
            setDarkLogoPreview(dark);
            setLightLogoRemoved(false);
            setDarkLogoRemoved(false);
        }
    }, [company]);

    // Same mechanism as signup: `GET /auth/countries` (see `Login.tsx` + `fetchCountriesAsync`)
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                setCountriesLoading(true);
                setCountriesError(null);
                const res = await apiClient.get<CountryOption[]>('/auth/countries');
                if (cancelled) return;
                const list = (res as any)?.data;
                setCountries(Array.isArray(list) ? list : []);
            } catch (e: any) {
                if (!cancelled) {
                    setCountries([]);
                    setCountriesError(typeof e?.message === 'string' ? e.message : 'Failed to load countries');
                }
            } finally {
                if (!cancelled) setCountriesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Fetch Worker Pool Names when dialog opens
    useEffect(() => {
        const fetchWorkerPoolNames = async (): Promise<void> => {
            try {
                setWorkerPoolNamesLoading(true);
                setWorkerPoolNamesError(null);
                const result = await (await import('../../../utils/ApiClient')).apiClient.get('/iu/workerpool/names');
                const names = (result as any)?.data || result || [];
                if (Array.isArray(names)) {
                    setWorkerPoolNames(names.map((n: any) => String(n)));
                } else {
                    setWorkerPoolNames([]);
                }
            } catch (e: any) {
                setWorkerPoolNamesError(typeof e?.message === 'string' ? e.message : 'Failed to load worker pool names');
                setWorkerPoolNames([]);
            } finally {
                setWorkerPoolNamesLoading(false);
            }
        };
        if (open) {
            fetchWorkerPoolNames();
        }
    }, [open]);

    // Reset form when dialog closes to avoid retaining previous company's values
    useEffect(() => {
        if (!open) {
            setFormData({
                name: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                country: '',
                zip: '',
                companyEmail: '',
                website: '',
                taxcode: '',
                companyPhone: '',
                registrationNumber: '',
                productPlans: [],
                configurations: {
                    defaultBotManager: ''
                }
            });
            setErrors({});
            setLightLogoPreview(undefined);
            setDarkLogoPreview(undefined);
            setLogoErrors({});
            setLogoWarnings({});
            setLightLogoRemoved(false);
            setDarkLogoRemoved(false);
        }
    }, [open]);

    // Real-time validation using useMemo
    const validationErrors = useMemo(() => {
        const newErrors: { [key: string]: string } = {};

        // Required field validations
        if (!formData.name?.trim()) newErrors.name = 'Company name is required';
        if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
        if (!formData.city?.trim()) newErrors.city = 'City is required';
        if (!formData.country?.trim()) newErrors.country = 'Country is required';
        if (!formData.zip?.trim()) newErrors.zip = 'ZIP code is required';

        // Email format validation
        if (formData.companyEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail.trim())) {
            newErrors.companyEmail = 'Please enter a valid email address';
        }

        // Phone validation: must start with '+' followed by country code digits
        if (formData.companyPhone?.trim() && !/^\+\d[\d\s\-()]{4,}$/.test(formData.companyPhone.trim())) {
            newErrors.companyPhone = 'Phone must start with country code (e.g. +44 7911 123456)';
        }

        // Product plans validation
        if (!formData.productPlans || formData.productPlans.length === 0) {
            newErrors.productPlans = 'At least one product plan is required';
        } else {
            formData.productPlans.forEach((plan: ProductPlan, index: number) => {
                if (!plan.licenseType?.trim()) {
                    newErrors[`productPlans.${index}.licenseType`] = 'License type is required';
                }
                if (!plan.module?.trim()) {
                    newErrors[`productPlans.${index}.module`] = 'Module is required';
                }
                if (!plan.startedAt?.trim()) {
                    newErrors[`productPlans.${index}.startedAt`] = 'Started date is required';
                }
                if (!plan.endsAt?.trim()) {
                    newErrors[`productPlans.${index}.endsAt`] = 'End date is required';
                }
                if (!plan.noOfUsers || plan.noOfUsers <= 0) {
                    newErrors[`productPlans.${index}.noOfUsers`] = 'Number of users must be greater than 0';
                }
                if (plan.module === 'assetsmanagement.ai') {
                    const value = plan.totalSizeInPlan;
                    if (value === undefined || value === null || Number.isNaN(Number(value)) || Number(value) < 0) {
                        newErrors[`productPlans.${index}.totalSizeInPlan`] = 'Cloud Storage Size is required';
                    }
                }
                if (plan.module === 'beamonscreen.com') {
                    const requiredBeamonFields: { field: keyof ProductPlan; label: string }[] = [
                        { field: 'totalScreens', label: 'Total Number of screens' },
                        { field: 'totalReels', label: 'Total Number of Reels' },
                        { field: 'totalPlaylists', label: 'Total Number of Playlists' },
                        { field: 'cloudSpaceInSizeInGB', label: 'Cloud Space Size' },
                    ];
                    requiredBeamonFields.forEach(({ field, label }) => {
                        const value = plan[field] as number | undefined;
                        if (value === undefined || value === null || Number.isNaN(Number(value)) || Number(value) < 0) {
                            newErrors[`productPlans.${index}.${field}`] = `${label} is required`;
                        }
                    });
                }
            });
        }

        return newErrors;
    }, [formData]);

    // Update errors state when validation changes
    useEffect(() => {
        setErrors(validationErrors);
    }, [validationErrors]);

    const handleInputChange = (field: keyof Company, value: string): void => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleConfigurationsChange = (field: keyof NonNullable<Company['configurations']>, value: string): void => {
        setFormData(prev => ({
            ...prev,
            configurations: {
                ...prev.configurations,
                [field]: value
            }
        }));
    };

    const handleProductPlanChange = (index: number, field: keyof ProductPlan, value: string | number | undefined): void => {
        setFormData(prev => {
            const updatedPlans = [...(prev.productPlans || [])];
            updatedPlans[index] = { ...updatedPlans[index], [field]: value };
            return { ...prev, productPlans: updatedPlans };
        });
    };

    // Update a sub-field (perDayLimit or totalCredit) within a nested quota object
    const handleQuotaLimitChange = (index: number, field: keyof ProductPlan, value: number | undefined, subField: string = 'perDayLimit'): void => {
        setFormData(prev => {
            const updatedPlans = [...(prev.productPlans || [])] as any[];
            const currentPlan = { ...(updatedPlans[index] || {}) } as any;
            const currentQuota = { ...(currentPlan[field] || {}) };

            if (value === undefined || value === null || value === ('' as any)) {
                delete currentQuota[subField];
            } else {
                currentQuota[subField] = value;
            }

            const hasPerDayLimit = currentQuota.perDayLimit !== undefined && currentQuota.perDayLimit !== null;
            const hasTotalCredit = currentQuota.totalCredit !== undefined && currentQuota.totalCredit !== null;
            currentPlan[field] = (hasPerDayLimit || hasTotalCredit) ? currentQuota : undefined;

            updatedPlans[index] = currentPlan;
            return { ...prev, productPlans: updatedPlans } as any;
        });
    };

    const addProductPlan = (): void => {
        const newPlan: ProductPlan = {
            licenseType: '',
            module: '',
            startedAt: '',
            endsAt: '',
            status: 'active',
            noOfUsers: 1
        };
        setFormData(prev => ({
            ...prev,
            productPlans: [...(prev.productPlans || []), newPlan]
        }));
    };

    const removeProductPlan = (index: number): void => {
        setFormData(prev => ({
            ...prev,
            productPlans: prev.productPlans?.filter((_, i) => i !== index) || []
        }));
    };

    const handleSave = (): void => {
        if (Object.keys(validationErrors).length === 0) {
            // Convert date formats back to ISO strings for API
            const processedFormData = { ...formData };
            if (processedFormData.productPlans) {
                processedFormData.productPlans = processedFormData.productPlans.map((plan: any) => {
                    // Remove _id if it's empty or doesn't exist (for new plans)
                    const { _id, ...planWithoutId } = plan;
                    const planData: any = (_id && typeof _id === 'string' && _id.trim() !== '') ? plan : planWithoutId;

                    // Normalize quota objects: send perDayLimit and totalCredit; do not include tracker fields
                    const pickQuotaLimits = (quota: any) => {
                        if (!quota) return undefined;
                        const result: any = {};
                        if (quota.perDayLimit !== undefined && quota.perDayLimit !== null && quota.perDayLimit !== '') {
                            result.perDayLimit = quota.perDayLimit;
                        }
                        if (quota.totalCredit !== undefined && quota.totalCredit !== null && quota.totalCredit !== '') {
                            result.totalCredit = quota.totalCredit;
                        }
                        return Object.keys(result).length > 0 ? result : undefined;
                    };

                    const cleaned: any = {
                        ...planData,
                        startedAt: formatDateForAPI(plan.startedAt),
                        endsAt: formatDateForAPI(plan.endsAt),
                        whatsApp: pickQuotaLimits(plan.whatsApp),
                        whatsAppTemplate: pickQuotaLimits(plan.whatsAppTemplate),
                        whatsAppBusiness: pickQuotaLimits(plan.whatsAppBusiness),
                        whatsAppBusinessTemplate: pickQuotaLimits(plan.whatsAppBusinessTemplate),
                        gsmText: pickQuotaLimits(plan.gsmText),
                        gsmTextTemplate: pickQuotaLimits(plan.gsmTextTemplate),
                        telegram: pickQuotaLimits(plan.telegram),
                        telegramTemplate: pickQuotaLimits(plan.telegramTemplate),
                        email: pickQuotaLimits(plan.email),
                        emailTemplate: pickQuotaLimits(plan.emailTemplate),
                    };

                    // Remove legacy sizeOfDAMinGB if present
                    delete cleaned.sizeOfDAMinGB;

                    // Remove legacy numeric fields if present to avoid saving them
                    delete cleaned.whatsAppMessagesAllowedPerDay;
                    delete cleaned.whatsAppTemplatesAllowedPerDay;
                    delete cleaned.gsmTextMessagesAllowedPerDay;
                    delete cleaned.gsmTextTemplatesAllowedPerDay;
                    delete cleaned.telegramMessagesPerDay;
                    delete cleaned.telegramTemplatesPerDay;
                    delete cleaned.emailsAllowedPerDay;
                    delete cleaned.emailTemplatesAllowedPerDAy;

                    // Set default senderHeaderName for instantupdate.ai when empty
                    if (plan.module === 'instantupdate.ai' && (cleaned.senderHeaderName === undefined || cleaned.senderHeaderName === '')) {
                        cleaned.senderHeaderName = '640807';
                    }

                    return cleaned as ProductPlan;
                });
            }
            
            // Attach brand logos from previews and explicit nulls for deletions
            if (lightLogoPreview || darkLogoPreview || lightLogoRemoved || darkLogoRemoved) {
                processedFormData.brand = processedFormData.brand || {} as any;
                (processedFormData.brand as any).logo = (processedFormData.brand as any).logo || {};
                if (lightLogoPreview) (processedFormData.brand as any).logo.light = lightLogoPreview;
                if (darkLogoPreview) (processedFormData.brand as any).logo.dark = darkLogoPreview;
                if (lightLogoRemoved && !lightLogoPreview) (processedFormData.brand as any).logo.light = null as any;
                if (darkLogoRemoved && !darkLogoPreview) (processedFormData.brand as any).logo.dark = null as any;
            }

            // Update the company object with processed form data
            if (company) {
                Object.assign(company, processedFormData);
            }
            onSave();
        }
    };

    const isSaveDisabled = saveLoading || Object.keys(validationErrors).length > 0 || !!logoErrors.light || !!logoErrors.dark;

    const countryValue = (formData.country || '').trim();
    const countryHelperText =
        errors.country ||
        countriesError ||
        (countriesLoading ? 'Loading countries…' : '');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEditing ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Company Name */}
                    <TextField
                        label="Company Name *"
                        value={formData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    {/* Address Information */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Address Line 1 *"
                            value={formData.addressLine1 || ''}
                            onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                            fullWidth
                            error={!!errors.addressLine1}
                            helperText={errors.addressLine1}
                        />

                        <TextField
                            label="Address Line 2"
                            value={formData.addressLine2 || ''}
                            onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                            fullWidth
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="City *"
                                value={formData.city || ''}
                                onChange={(e) => handleInputChange('city', e.target.value)}
                                sx={{ flex: 1 }}
                                error={!!errors.city}
                                helperText={errors.city}
                            />

                            <TextField
                                label="State / County"
                                value={formData.state || ''}
                                onChange={(e) => handleInputChange('state', e.target.value)}
                                sx={{ flex: 1 }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Autocomplete
                                options={countries}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                        const found = countries.find((c) => c.code === option);
                                        return found ? `${found.label} (${found.dialCode})` : option;
                                    }
                                    return `${option.label} (${option.dialCode})`;
                                }}
                                value={countries.find((c) => c.code === countryValue) || null}
                                onChange={(_, newValue) => {
                                    handleInputChange('country', newValue ? newValue.code : '');
                                }}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                                filterOptions={(options, state) => {
                                    const input = state.inputValue.toLowerCase().trim();
                                    if (!input) return options;
                                    return options.filter(
                                        (o) =>
                                            o.label.toLowerCase().includes(input) ||
                                            o.code.toLowerCase().includes(input) ||
                                            o.dialCode.includes(input)
                                    );
                                }}
                                loading={countriesLoading}
                                disabled={countriesLoading}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.code}>
                                        {option.label} ({option.dialCode})
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Country *"
                                        placeholder="Search country..."
                                        error={!!errors.country || !!countriesError}
                                        helperText={countryHelperText}
                                    />
                                )}
                                sx={{ flex: 1 }}
                            />

                            <TextField
                                label="ZIP Code *"
                                value={formData.zip || ''}
                                onChange={(e) => handleInputChange('zip', e.target.value)}
                                sx={{ flex: 1 }}
                                error={!!errors.zip}
                                helperText={errors.zip}
                            />
                        </Box>
                    </Box>

                    {/* Contact Information */}
                    <TextField
                        label="Company Email"
                        type="email"
                        value={formData.companyEmail || ''}
                        onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                        fullWidth
                        error={!!errors.companyEmail}
                        helperText={errors.companyEmail}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Website"
                            value={formData.website || ''}
                            onChange={(e) => handleInputChange('website', e.target.value)}
                            sx={{ flex: 1 }}
                        />

                        <TextField
                            label="Company Phone"
                            value={formData.companyPhone || ''}
                            onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                            sx={{ flex: 1 }}
                            error={!!errors.companyPhone}
                            helperText={errors.companyPhone || 'Must start with country code (e.g. +44...)'}
                            placeholder="+1 555 123 4567"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Tax Code"
                            value={formData.taxcode || ''}
                            onChange={(e) => handleInputChange('taxcode', e.target.value)}
                            sx={{ flex: 1 }}
                        />

                        <TextField
                            label="Registration Number"
                            value={formData.registrationNumber || ''}
                            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                            sx={{ flex: 1 }}
                        />
                    </Box>

                    {/* Brand Logos */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Brand Logos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        PNG required; recommended size 200px × 65px. Other sizes are allowed.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle2">Light Theme Logo</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant="rounded" sx={{ width: 80, height: 80 }} src={lightLogoPreview} />
                                <Button variant="outlined" component="label">
                                    Upload
                                    <input
                                        hidden
                                        accept="image/*"
                                        type="file"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            // Validate type (PNG only)
                                            if (file.type !== 'image/png') {
                                                setLogoErrors(prev => ({ ...prev, light: 'Only PNG files are allowed.' }));
                                                return;
                                            }
                                            const reader = new FileReader();
                                            reader.onloadend = () => {
                                                const result = reader.result as string;
                                                const img = new Image();
                                                img.onload = () => {
                                                    // Non-blocking size guidance
                                                    if (img.width !== 200 || img.height !== 65) {
                                                        setLogoWarnings(prev => ({ ...prev, light: `Recommended 200×65. Uploaded: ${img.width}×${img.height}.` }));
                                                    } else {
                                                        setLogoWarnings(prev => ({ ...prev, light: '' }));
                                                    }
                                                    setLogoErrors(prev => ({ ...prev, light: '' }));
                                                    setLightLogoPreview(result);
                                                    setLightLogoRemoved(false);
                                                };
                                                img.onerror = () => setLogoErrors(prev => ({ ...prev, light: 'Invalid image file.' }));
                                                img.src = result;
                                            };
                                            reader.readAsDataURL(file);
                                        }}
                                    />
                                </Button>
                                {lightLogoPreview && (
                                    <Button color="error" variant="text" onClick={() => { setLightLogoPreview(undefined); setLogoWarnings(prev => ({ ...prev, light: '' })); setLogoErrors(prev => ({ ...prev, light: '' })); setLightLogoRemoved(true); }}>
                                        Remove
                                    </Button>
                                )}
                            </Stack>
                            {logoErrors.light && (
                                <FormHelperText sx={{ color: '#f44336' }}>{logoErrors.light}</FormHelperText>
                            )}
                            {!logoErrors.light && logoWarnings.light && (
                                <FormHelperText sx={{ color: 'text.secondary' }}>{logoWarnings.light}</FormHelperText>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle2">Dark Theme Logo</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant="rounded" sx={{ width: 80, height: 80, bgcolor: 'grey.900' }} src={darkLogoPreview} />
                                <Button variant="outlined" component="label">
                                    Upload
                                    <input
                                        hidden
                                        accept="image/*"
                                        type="file"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            // Validate type (PNG only)
                                            if (file.type !== 'image/png') {
                                                setLogoErrors(prev => ({ ...prev, dark: 'Only PNG files are allowed.' }));
                                                return;
                                            }
                                            const reader = new FileReader();
                                            reader.onloadend = () => {
                                                const result = reader.result as string;
                                                const img = new Image();
                                                img.onload = () => {
                                                    // Non-blocking size guidance
                                                    if (img.width !== 200 || img.height !== 65) {
                                                        setLogoWarnings(prev => ({ ...prev, dark: `Recommended 200×65. Uploaded: ${img.width}×${img.height}.` }));
                                                    } else {
                                                        setLogoWarnings(prev => ({ ...prev, dark: '' }));
                                                    }
                                                    setLogoErrors(prev => ({ ...prev, dark: '' }));
                                                    setDarkLogoPreview(result);
                                                    setDarkLogoRemoved(false);
                                                };
                                                img.onerror = () => setLogoErrors(prev => ({ ...prev, dark: 'Invalid image file.' }));
                                                img.src = result;
                                            };
                                            reader.readAsDataURL(file);
                                        }}
                                    />
                                </Button>
                                {darkLogoPreview && (
                                    <Button color="error" variant="text" onClick={() => { setDarkLogoPreview(undefined); setLogoWarnings(prev => ({ ...prev, dark: '' })); setLogoErrors(prev => ({ ...prev, dark: '' })); setDarkLogoRemoved(true); }}>
                                        Remove
                                    </Button>
                                )}
                            </Stack>
                            {logoErrors.dark && (
                                <FormHelperText sx={{ color: '#f44336' }}>{logoErrors.dark}</FormHelperText>
                            )}
                            {!logoErrors.dark && logoWarnings.dark && (
                                <FormHelperText sx={{ color: 'text.secondary' }}>{logoWarnings.dark}</FormHelperText>
                            )}
                        </Box>
                    </Stack>

                    {/* Bot Manager Configuration */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Bot Manager Configuration
                    </Typography>

                    <Autocomplete
                        freeSolo
                        options={['ClientManaged']}
                        value={formData.configurations?.defaultBotManager || ''}
                        inputValue={formData.configurations?.defaultBotManager || ''}
                        onChange={(_, newValue) => {
                            handleConfigurationsChange('defaultBotManager', newValue || '');
                        }}
                        onInputChange={(_, newInputValue) => {
                            handleConfigurationsChange('defaultBotManager', newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Default Bot Manager"
                                placeholder="Enter bot manager type or select ClientManaged"
                                helperText="Optional: Specify the default bot manager type. Use 'ClientManaged' for client-managed bots or enter a custom value."
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span" sx={{ color: 'primary.main' }}>🤖</Box>
                                    {option}
                                </Box>
                            </Box>
                        )}
                    />

                    {/* Product Plans Section */}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Product Plans *
                    </Typography>

                    {errors.productPlans && (
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                            {errors.productPlans}
                        </Typography>
                    )}

                    {(formData.productPlans || []).map((plan: ProductPlan, index: number) => (
                        <ProductPlanPanel
                            key={index}
                            index={index}
                            plan={plan as any}
                            onFieldChange={(i, field, value) => handleProductPlanChange(i, field as keyof ProductPlan, value)}
                            onQuotaChange={(i, field, value, subField) => handleQuotaLimitChange(i, field as keyof ProductPlan, value, subField)}
                            onRemove={removeProductPlan}
                            licenseTypes={[...LICENSE_TYPES]}
                            modules={availableModules}
                            modulesLoading={modulesLoading}
                            modulesError={modulesError}
                            errors={errors}
                            variant="company"
                            workerPoolNames={workerPoolNames}
                            workerPoolNamesLoading={workerPoolNamesLoading}
                            workerPoolNamesError={workerPoolNamesError}
                        />
                    ))}

                    <Button
                        onClick={addProductPlan}
                        startIcon={<Add />}
                        variant="outlined"
                        color="secondary"
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Add Product Plan
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    variant="outlined"
                    color="secondary" 
                    onClick={onClose} 
                    startIcon={<Cancel />} 
                    disabled={saveLoading}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    startIcon={<Save />} 
                    variant="contained" 
                    color="success" 
                    disabled={isSaveDisabled}
                >
                    {saveLoading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
